import React, { useState, useEffect } from 'react';
import { createContact } from '../../api/contacts';
import { useUpdateContact } from '../../hooks/useContacts';
import { Contact } from '../../types';
import { CompanyAutocomplete } from '../shared/CompanyAutocomplete';

interface ContactFormProps {
  companyId?: string;
  onSuccess: () => void;
  onCancel: () => void;
  initial?: Contact;
}

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 12px', border: '1px solid var(--border2)',
  borderRadius: 'var(--radius)', fontSize: '13px', background: 'var(--surface)', color: 'var(--text)',
};
const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: '11px', fontWeight: 500,
  textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--text3)', marginBottom: '5px',
};

const MASKED = '●●●●●●';
const isMasked = (v?: string | null) => v === MASKED || v?.startsWith('●');

export function ContactForm({ companyId, onSuccess, onCancel, initial }: ContactFormProps) {
  const isEdit = !!initial;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const updateContactMutation = useUpdateContact();

  const [form, setForm] = useState({
    display_name: initial?.display_name ?? '',
    first_name: initial?.first_name ?? '',
    last_name: initial?.last_name ?? '',
    salutation: initial?.salutation ?? '',
    title: initial?.title ?? '',
    job_title: initial?.job_title ?? '',
    company_id: initial?.company_id ?? companyId ?? '',
    company_name: (initial as Contact & { company_name?: string })?.company_name ?? '',
    // Maskierte Werte NICHT vorbelegen — leer lassen damit User weiß: Feld ist verschlüsselt
    email: isMasked(initial?.email) ? '' : (initial?.email ?? ''),
    phone: isMasked(initial?.phone) ? '' : (initial?.phone ?? ''),
    mobile_private: isMasked(initial?.mobile_private) ? '' : (initial?.mobile_private ?? ''),
    propstack_tags: initial?.propstack_tags ?? '',
    kontakt_rolle: initial?.is_primary_contact
      ? 'hauptansprechpartner'
      : initial?.is_postempfaenger
      ? 'postempfaenger'
      : 'keine',
  });

  const set = (field: string, value: string | boolean) =>
    setForm(f => ({ ...f, [field]: value }));

  // Auto-fill display_name from first+last (ohne Anrede) — auch im Edit-Modus
  useEffect(() => {
    const full = [form.first_name, form.last_name].filter(Boolean).join(' ');
    if (full) set('display_name', full);
  }, [form.first_name, form.last_name]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // display_name = Vorname + Nachname (ohne Anrede)
    const dn = [form.first_name, form.last_name].filter(Boolean).join(' ')
      || form.display_name.trim();
    if (!dn) { setError('Name ist Pflichtfeld'); return; }
    setLoading(true); setError(null);
    try {
      const payload: import('../../api/contacts').ContactCreateInput = {
        display_name: dn,
        first_name: form.first_name.trim() || undefined,
        last_name: form.last_name.trim() || undefined,
        salutation: form.salutation || undefined,
        title: form.title.trim() || undefined,
        job_title: form.job_title.trim() || undefined,
        company_id: form.company_id || undefined,
        propstack_tags: form.propstack_tags.trim() || undefined,
        is_primary_contact: form.kontakt_rolle === 'hauptansprechpartner',
        is_postempfaenger: form.kontakt_rolle === 'postempfaenger',
      };
      // Nur senden wenn der User tatsächlich etwas eingetragen hat (nie maskierte Werte)
      if (form.email.trim() && !isMasked(form.email)) payload.email = form.email.trim();
      if (form.phone.trim() && !isMasked(form.phone)) payload.phone = form.phone.trim();
      if (form.mobile_private.trim() && !isMasked(form.mobile_private)) payload.mobile_private = form.mobile_private.trim();

      if (isEdit && initial) {
        await updateContactMutation.mutateAsync({ id: initial.id, input: payload });
      } else {
        await createContact(payload);
      }
      onSuccess();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } }).response?.data?.error;
      setError(msg ?? (err instanceof Error ? err.message : 'Fehler beim Speichern'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div style={{ display: 'grid', gap: '14px' }}>

        {/* Anrede + Titel + Vorname + Nachname */}
        <div style={{ display: 'grid', gridTemplateColumns: '90px 130px 1fr 1fr', gap: '10px' }}>
          <div>
            <label style={labelStyle}>Anrede</label>
            <select style={inputStyle} value={form.salutation} onChange={e => set('salutation', e.target.value)}>
              <option value="">—</option>
              <option>Herr</option>
              <option>Frau</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>Titel</label>
            <input
              style={inputStyle}
              value={form.title}
              onChange={e => set('title', e.target.value)}
              list="contact-title-options"
              placeholder="z.B. Dr."
            />
            <datalist id="contact-title-options">
              <option value="Dr." />
              <option value="Prof." />
              <option value="Prof. Dr." />
              <option value="Dipl.-Ing." />
              <option value="Dipl.-Kfm." />
              <option value="Dr. h.c." />
              <option value="MBA" />
              <option value="LL.M." />
            </datalist>
          </div>
          <div>
            <label style={labelStyle}>Vorname</label>
            <input style={inputStyle} value={form.first_name}
              onChange={e => set('first_name', e.target.value)} placeholder="Max" />
          </div>
          <div>
            <label style={labelStyle}>Nachname *</label>
            <input style={inputStyle} value={form.last_name}
              onChange={e => set('last_name', e.target.value)} placeholder="Mustermann" autoFocus />
          </div>
        </div>

        {/* Position */}
        <div>
          <label style={labelStyle}>Position / Titel</label>
          <input style={inputStyle} value={form.job_title}
            onChange={e => set('job_title', e.target.value)}
            placeholder="z.B. Managing Director, Investment Manager" />
        </div>

        {/* Firma zuordnen — Autocomplete (immer sichtbar im Edit-Modus) */}
        {(!companyId || isEdit) && (
          <div>
            <label style={labelStyle}>Firma zuordnen</label>
            <CompanyAutocomplete
              value={form.company_id}
              initialName={form.company_name || undefined}
              onChange={(id, name) => {
                set('company_id', id);
                set('company_name', name);
              }}
            />
          </div>
        )}
        {companyId && !isEdit && (
          <div style={{ padding: '8px 12px', background: 'var(--surface2)', borderRadius: 'var(--radius)', fontSize: '12px', color: 'var(--text2)' }}>
            Wird der Firma zugeordnet
          </div>
        )}

        {/* Kontaktdaten */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
          <div>
            <label style={labelStyle}>E-Mail{isEdit && initial?.email && isMasked(initial.email) && <span style={{ color: 'var(--text3)', fontWeight: 400 }}> (verschlüsselt — neu eingeben zum Ändern)</span>}</label>
            <input style={inputStyle} type="email" value={form.email}
              onChange={e => set('email', e.target.value)}
              placeholder={isEdit && isMasked(initial?.email) ? '••• neu eingeben •••' : 'max@beispiel.de'} />
          </div>
          <div>
            <label style={labelStyle}>Telefon{isEdit && initial?.phone && isMasked(initial.phone) && <span style={{ color: 'var(--text3)', fontWeight: 400 }}> (verschlüsselt)</span>}</label>
            <input style={inputStyle} type="tel" value={form.phone}
              onChange={e => set('phone', e.target.value)}
              placeholder={isEdit && isMasked(initial?.phone) ? '••• neu eingeben •••' : '+49 69 ...'} />
          </div>
        </div>

        {/* Tags */}
        <div>
          <label style={labelStyle}>Tags (kommagetrennt)</label>
          <input style={inputStyle} value={form.propstack_tags}
            onChange={e => set('propstack_tags', e.target.value)}
            placeholder="Kaufinteressent,Hotel" />
        </div>

        {/* Ansprechpartner-Rolle */}
        <div>
          <label style={labelStyle}>Rolle</label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {([
              { value: 'hauptansprechpartner', label: 'Hauptansprechpartner' },
              { value: 'postempfaenger', label: 'Postempfänger (erhält postalische Sendungen)' },
              { value: 'keine', label: 'Keine Kommunikation' },
            ] as const).map(opt => (
              <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="kontakt_rolle"
                  value={opt.value}
                  checked={form.kontakt_rolle === opt.value}
                  onChange={() => set('kontakt_rolle', opt.value)}
                  style={{ accentColor: 'var(--accent)', cursor: 'pointer' }}
                />
                <span style={{ fontSize: '13px' }}>{opt.label}</span>
              </label>
            ))}
          </div>
        </div>

        {error && (
          <div style={{ padding: '10px 14px', background: 'var(--accent-light)', color: 'var(--accent)', borderRadius: 'var(--radius)', fontSize: '13px' }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', paddingTop: '4px' }}>
          <button type="button" onClick={onCancel} disabled={loading}
            style={{ padding: '8px 16px', background: 'var(--surface)', border: '1px solid var(--border2)', borderRadius: 'var(--radius)', fontSize: '13px', cursor: 'pointer' }}>
            Abbrechen
          </button>
          <button type="submit" disabled={loading}
            style={{ padding: '8px 20px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 'var(--radius)', fontSize: '13px', fontWeight: 500, cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1 }}>
            {loading ? 'Wird gespeichert…' : isEdit ? 'Änderungen speichern' : 'Kontakt anlegen'}
          </button>
        </div>
      </div>
    </form>
  );
}
